import { useEffect, useRef } from 'react';
import { angularInjectorProvider } from 'Injector';
import { type ReactRouterBridge } from 'Navigation/angularModule/scripts/react_router_bridge';

const UNSAVED_CHANGES_UNLOAD_MESSAGE = 'You have unsaved changes';
export const UNSAVED_CHANGES_CONFIRM_MESSAGE = 'You have unsaved changes.  Are you sure you want to leave this page?';

// Angular's UnsavedChangesWarning waits this long before it warns a second time. Chrome can fire
// beforeunload and then, if the user cancels, a route change follows. Without the window the user
// answers two prompts for one action.
const WARNING_SILENCED_MS = 5000;

function pathOf(url: string) {
    return url.split('?')[0];
}

/*
    Warns before leaving the page with unsaved changes. Two things it must not do:

    - It must not assign window.onbeforeunload. FrontRoyalStatsig registers its own listener there.
    - It must not register a second useBlocker. FrontRoyalRouter/useAllowBlockNavigation owns the
      app's blocker and turns a preventDefault on this event into blocker.reset().
*/
export function useUnsavedChangesWarning(isDirty: boolean) {
    const isDirtyRef = useRef(isDirty);
    const lastWarningAtRef = useRef<number | null>(null);

    isDirtyRef.current = isDirty;

    useEffect(() => {
        const warningSilenced = () =>
            !!lastWarningAtRef.current && Date.now() - lastWarningAtRef.current < WARNING_SILENCED_MS;

        const shouldWarn = () => isDirtyRef.current && !warningSilenced();

        // addEventListener rather than an assignment to window.onbeforeunload, which would clobber
        // the handler FrontRoyalStatsig registers.
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (!shouldWarn()) return;
            lastWarningAtRef.current = Date.now();
            event.preventDefault();
            event.returnValue = UNSAVED_CHANGES_UNLOAD_MESSAGE;
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        const bridge = angularInjectorProvider.injector?.get<ReactRouterBridge>('reactRouterBridge');
        const stopListening = bridge?.on('routeChangeStart', event => {
            // A change to the query string alone is not leaving the page.
            if (pathOf(event.nextUrl) === pathOf(event.currentUrl)) return;
            if (!shouldWarn()) return;

            lastWarningAtRef.current = Date.now();
            if (!window.confirm(UNSAVED_CHANGES_CONFIRM_MESSAGE)) event.preventDefault();
        });

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            stopListening?.();
        };
    }, []);
}
